import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { AlertCircle, Check, ChevronsUpDown, Loader2, Mail, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '~/api/trpc';
import { BillingDraftAddressFields } from '~/components/billing-draft-creator/BillingDraftAddressFields';
import {
    cancellationFeeAppointmentStatusLabels,
    cancellationFeeTypeLabels,
    formatDurationRange,
    formatEur,
    hasAppointmentEnded,
} from '~/lib/cancellation-fee-config';
import { cn } from '~/lib/utils';

const createCancellationFeeSchema = z.object({
    customerId: z.number().int().positive('Kunde erforderlich'),
    appointmentId: z.string().min(1, 'Termin erforderlich'),
    cancellationFeeTierId: z.string().min(1, 'Gebührenstufe erforderlich'),
    unitPriceGross: z.string().trim().refine((value) => Number(value) > 0, 'Betrag muss größer als 0 sein'),
    companyName: z.string().min(1, 'Firmenname erforderlich'),
    companyAddress: z.string().min(1, 'Adresse erforderlich'),
    companyCity: z.string().min(1, 'PLZ / Stadt erforderlich'),
    companyCountry: z.string().min(1, 'Land erforderlich'),
    customerName: z.string().min(1, 'Name erforderlich'),
    customerNumber: z.string(),
    customerStreet: z.string(),
    customerZipcode: z.string(),
    customerCity: z.string(),
    customerCountry: z.string(),
});

type CreateCancellationFeeFormData = z.infer<typeof createCancellationFeeSchema>;

const emptyAddress = {
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyCountry: 'Deutschland',
    customerName: '',
    customerNumber: '',
    customerStreet: '',
    customerZipcode: '',
    customerCity: '',
    customerCountry: 'Deutschland',
};

export type SelectedCustomer = {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
};

type AppointmentOption = {
    title: string;
    status: string;
    startDate: string | Date | null;
    endDate: string | Date | null;
    durationMinutes: number;
    store: { name: string } | null;
    activeCancellationFeeDraft: { billingDraftNumber: string } | null;
};

const AppointmentOptionContent = ({ appointment }: { appointment: AppointmentOption }) => {
    const statusInfo = cancellationFeeAppointmentStatusLabels[appointment.status]
        ?? { label: appointment.status, variant: 'outline' as const };
    return (
        <div className='flex flex-1 min-w-0 items-center justify-between gap-3 text-left'>
            <div className='min-w-0'>
                <div className='font-medium truncate'>{appointment.title}</div>
                <div className='text-sm text-muted-foreground'>
                    {appointment.startDate
                        ? format(new Date(appointment.startDate), "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de })
                        : 'Kein Datum'}
                    {' · '}{appointment.durationMinutes} Min.
                    {appointment.store ? ` · ${appointment.store.name}` : ''}
                </div>
                {appointment.activeCancellationFeeDraft && (
                    <div className='text-xs text-muted-foreground'>
                        Bereits abgerechnet ({appointment.activeCancellationFeeDraft.billingDraftNumber})
                    </div>
                )}
                {!appointment.activeCancellationFeeDraft && !hasAppointmentEnded(appointment.endDate) && (
                    <div className='text-xs text-muted-foreground'>
                        {appointment.endDate
                            ? `Termin noch nicht beendet – ab ${format(new Date(appointment.endDate), "dd.MM.yyyy HH:mm 'Uhr'", { locale: de })} möglich`
                            : 'Termin ohne Terminzeiten'}
                    </div>
                )}
            </div>
            <Badge variant={statusInfo.variant} className='shrink-0'>{statusInfo.label}</Badge>
        </div>
    );
};

export type CreateCancellationFeePrefill = {
    customer: SelectedCustomer;
    appointmentId: string;
};

type CreateCancellationFeeDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    prefill?: CreateCancellationFeePrefill | null;
};

export const CreateCancellationFeeDialog = ({ open, onOpenChange, prefill }: CreateCancellationFeeDialogProps) => {
    const navigate = useNavigate();
    const trpcUtils = trpc.useUtils();
    const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
    const [appointmentSelectOpen, setAppointmentSelectOpen] = useState(false);

    const form = useForm<CreateCancellationFeeFormData>({
        resolver: zodResolver(createCancellationFeeSchema),
        defaultValues: {
            customerId: 0,
            appointmentId: '',
            cancellationFeeTierId: '',
            unitPriceGross: '',
            ...emptyAddress,
        },
    });

    const { watch, setValue, reset } = form;
    const selectedCustomerId = watch('customerId');
    const selectedAppointmentId = watch('appointmentId');
    const selectedTierId = watch('cancellationFeeTierId');
    const unitPriceGross = watch('unitPriceGross');

    useEffect(() => {
        if (!open) {
            reset();
            setCustomerSearchTerm('');
            setSelectedCustomer(null);
        }
    }, [open, reset]);

    useEffect(() => {
        if (open && prefill) {
            setSelectedCustomer(prefill.customer);
            setValue('customerId', prefill.customer.id);
            setValue('appointmentId', prefill.appointmentId);
        }
    }, [open, prefill, setValue]);

    const { data: customersData } = trpc.customers.getAll.useQuery(
        { page: 0, pageSize: 20, filters: { name: customerSearchTerm } },
        { enabled: open }
    );

    const { data: customerInfo } = trpc.billingDrafts.getCustomerAddressInfo.useQuery(
        { customerId: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    const { data: appointments, isLoading: appointmentsLoading } = trpc.cancellationFees.getEligibleAppointments.useQuery(
        { customerId: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    const selectedAppointment = appointments?.find((apt) => apt.id === selectedAppointmentId) ?? null;
    const selectedStoreId = selectedAppointment?.store?.id ?? 0;

    const { data: companyInfo } = trpc.billingDrafts.getCompanyDetailsForStore.useQuery(
        { storeId: selectedStoreId },
        { enabled: selectedStoreId > 0 }
    );

    const { data: tiersData, isLoading: tiersLoading } = trpc.cancellationFees.getTiersForAppointment.useQuery(
        { appointmentId: selectedAppointmentId },
        { enabled: !!selectedAppointmentId }
    );

    useEffect(() => {
        if (customerInfo) {
            setValue('customerName', customerInfo.customerName);
            setValue('customerNumber', customerInfo.customerNumber);
            setValue('customerStreet', customerInfo.customerStreet);
            setValue('customerZipcode', customerInfo.customerZipcode);
            setValue('customerCity', customerInfo.customerCity);
            setValue('customerCountry', customerInfo.customerCountry);
        }
    }, [customerInfo, setValue]);

    useEffect(() => {
        if (companyInfo) {
            setValue('companyName', companyInfo.companyName);
            setValue('companyAddress', companyInfo.companyAddress);
            setValue('companyCity', companyInfo.companyCity);
            setValue('companyCountry', companyInfo.companyCountry);
        }
    }, [companyInfo, setValue]);

    const cartTotalGross = tiersData?.cartTotalGross ?? null;

    // The fee defaults to the tier price, but never more than the undiscounted cart total.
    const selectTier = useCallback((tierId: string) => {
        setValue('cancellationFeeTierId', tierId);
        const tier = tiersData?.tiers.find((t) => t.id === tierId);
        const price = tier ? Math.min(tier.unitPriceGross, tiersData?.cartTotalGross ?? Infinity) : null;
        setValue('unitPriceGross', price !== null ? String(price) : '', { shouldValidate: price !== null });
    }, [tiersData, setValue]);

    // Preselect the tier matching the appointment length whenever the appointment changes.
    useEffect(() => {
        selectTier(tiersData?.suggestedTierId ?? '');
    }, [tiersData, selectTier]);

    const createAndSendMutation = trpc.cancellationFees.createAndSend.useMutation({
        onSuccess: (result) => {
            if (result.sent) {
                toast.success(`Zahlungslink für Ausfallentschädigung ${result.billingDraftNumber} wurde an ${result.email} gesendet`);
            } else {
                toast.warning(result.warning ?? 'Der Zahlungslink konnte nicht versendet werden');
            }
            trpcUtils.billingDrafts.getAll.invalidate();
            trpcUtils.cancellationFees.getEligibleAppointments.invalidate();
            trpcUtils.cancellationFees.getTodaysAppointments.invalidate();
            onOpenChange(false);
            navigate(`/billing-draft/${result.billingDraftId}`);
        },
        onError: (error) => {
            toast.error(error.message || 'Ausfallentschädigung konnte nicht erstellt werden');
        },
    });

    const recipientEmail = selectedCustomer?.email ?? null;
    const selectedTier = tiersData?.tiers.find((tier) => tier.id === selectedTierId) ?? null;
    const priceValue = Math.round(Number(unitPriceGross) * 100) / 100;
    const isPriceValid = Number.isFinite(priceValue) && priceValue > 0;
    const priceExceedsCart = isPriceValid && cartTotalGross !== null && priceValue > cartTotalGross;
    const isTierCappedByCart = !!selectedTier && cartTotalGross !== null && selectedTier.unitPriceGross > cartTotalGross;
    const canSubmit = !!recipientEmail
        && !!selectedAppointment
        && !selectedAppointment.hasActiveCancellationFee
        && hasAppointmentEnded(selectedAppointment.endDate)
        && !!selectedTier
        && isPriceValid
        && !priceExceedsCart
        && !createAndSendMutation.isPending;

    const onSubmit = (data: CreateCancellationFeeFormData) => {
        if (priceExceedsCart) return;
        const { customerId: _customerId, unitPriceGross: _unitPriceGross, ...payload } = data;
        createAndSendMutation.mutate({ ...payload, unitPriceGross: priceValue });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
                <DialogHeader>
                    <DialogTitle>Neue Ausfallentschädigung</DialogTitle>
                    <DialogDescription>
                        Erstellt einen Auftrag mit der Ausfallentschädigung für einen abgesagten oder nicht wahrgenommenen Termin und sendet dem Kunden direkt einen Zahlungslink mit der Rechnung.
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                        {/* Customer */}
                        <FormField
                            control={form.control}
                            name='customerId'
                            render={({ field }) => (
                                <FormItem className='flex flex-col'>
                                    <FormLabel>Kunde *</FormLabel>
                                    <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                                        <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                    variant='outline'
                                                    role='combobox'
                                                    aria-expanded={customerSearchOpen}
                                                    className='w-full justify-between'
                                                >
                                                    {selectedCustomer
                                                        ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                                                        : 'Kunde auswählen...'}
                                                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                                </Button>
                                            </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className='w-[400px] p-0'>
                                            <Command shouldFilter={false}>
                                                <CommandInput
                                                    placeholder='Kunde suchen...'
                                                    value={customerSearchTerm}
                                                    onValueChange={setCustomerSearchTerm}
                                                />
                                                <CommandList>
                                                    <CommandEmpty>Kein Kunde gefunden.</CommandEmpty>
                                                    <CommandGroup>
                                                        {customersData?.data.map((customer) => (
                                                            <CommandItem
                                                                key={customer.id}
                                                                value={String(customer.id)}
                                                                onSelect={() => {
                                                                    field.onChange(customer.id);
                                                                    setSelectedCustomer({
                                                                        id: customer.id,
                                                                        firstName: customer.firstName,
                                                                        lastName: customer.lastName,
                                                                        email: customer.email ?? null,
                                                                    });
                                                                    setValue('appointmentId', '');
                                                                    setValue('cancellationFeeTierId', '');
                                                                    setValue('unitPriceGross', '');
                                                                    setCustomerSearchOpen(false);
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        'mr-2 h-4 w-4',
                                                                        field.value === customer.id ? 'opacity-100' : 'opacity-0'
                                                                    )}
                                                                />
                                                                <div>
                                                                    <div className='font-medium'>
                                                                        {customer.firstName} {customer.lastName}
                                                                    </div>
                                                                    {customer.email && (
                                                                        <div className='text-sm text-muted-foreground'>
                                                                            {customer.email}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Appointment */}
                        {selectedCustomerId > 0 && (
                            <FormField
                                control={form.control}
                                name='appointmentId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Termin *</FormLabel>
                                        {appointmentsLoading ? (
                                            <Skeleton className='h-14 w-full' />
                                        ) : !appointments || appointments.length === 0 ? (
                                            <div className='rounded-md border border-dashed p-4 text-sm text-muted-foreground'>
                                                Dieser Kunde hat keine abgesagten oder nicht wahrgenommenen Termine.
                                            </div>
                                        ) : (
                                            <Popover open={appointmentSelectOpen} onOpenChange={setAppointmentSelectOpen}>
                                                <PopoverTrigger asChild>
                                                    <FormControl>
                                                        <Button
                                                            variant='outline'
                                                            role='combobox'
                                                            aria-expanded={appointmentSelectOpen}
                                                            className='w-full h-auto min-h-10 justify-between py-2 font-normal'
                                                        >
                                                            {selectedAppointment ? (
                                                                <AppointmentOptionContent appointment={selectedAppointment} />
                                                            ) : (
                                                                <span className='text-muted-foreground'>Termin auswählen...</span>
                                                            )}
                                                            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                                        </Button>
                                                    </FormControl>
                                                </PopoverTrigger>
                                                <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0' align='start'>
                                                    <Command>
                                                        <CommandList>
                                                            <CommandGroup>
                                                                {appointments.map((apt) => (
                                                                    <CommandItem
                                                                        key={apt.id}
                                                                        value={apt.id}
                                                                        disabled={apt.hasActiveCancellationFee || !hasAppointmentEnded(apt.endDate)}
                                                                        onSelect={() => {
                                                                            if (apt.id !== field.value) {
                                                                                field.onChange(apt.id);
                                                                                setValue('cancellationFeeTierId', '');
                                                                                setValue('unitPriceGross', '');
                                                                            }
                                                                            setAppointmentSelectOpen(false);
                                                                        }}
                                                                    >
                                                                        <Check
                                                                            className={cn(
                                                                                'mr-2 h-4 w-4 shrink-0',
                                                                                field.value === apt.id ? 'opacity-100' : 'opacity-0'
                                                                            )}
                                                                        />
                                                                        <AppointmentOptionContent appointment={apt} />
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        {/* Fee tier and amount */}
                        {selectedAppointment && (
                            <div className='grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]'>
                                <FormField
                                    control={form.control}
                                    name='cancellationFeeTierId'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Ausfallentschädigung *
                                                {tiersData?.feeType && (
                                                    <span className='ml-2 font-normal text-muted-foreground'>
                                                        ({cancellationFeeTypeLabels[tiersData.feeType]})
                                                    </span>
                                                )}
                                            </FormLabel>
                                            {tiersLoading ? (
                                                <Skeleton className='h-10 w-full' />
                                            ) : !tiersData || tiersData.tiers.length === 0 ? (
                                                <div className='rounded-md border border-dashed p-4 text-sm text-muted-foreground'>
                                                    Keine aktiven Gebührenstufen für diesen Termin vorhanden. Bitte zuerst unter „Gebührenstufen" anlegen.
                                                </div>
                                            ) : (
                                                <Select value={field.value} onValueChange={(value) => value && selectTier(value)}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder='Gebührenstufe auswählen...' />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {tiersData.tiers.map((tier) => (
                                                            <SelectItem key={tier.id} value={tier.id}>
                                                                {tier.name} · {formatDurationRange(tier.minDurationMinutes, tier.maxDurationMinutes)} · {formatEur(tier.unitPriceGross)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                            {tiersData && tiersData.tiers.length > 0 && !tiersData.suggestedTierId && (
                                                <p className='text-sm text-muted-foreground'>
                                                    Keine Gebührenstufe passt zur Termindauer von {tiersData.durationMinutes} Min. Bitte manuell auswählen.
                                                </p>
                                            )}
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='unitPriceGross'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Betrag brutto (€) *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type='number'
                                                    min={0.01}
                                                    max={cartTotalGross ?? undefined}
                                                    step={0.01}
                                                    disabled={!selectedTier}
                                                    {...field}
                                                />
                                            </FormControl>
                                            {priceExceedsCart && cartTotalGross !== null ? (
                                                <p className='text-sm font-medium text-destructive'>
                                                    Darf den Warenkorbwert von {formatEur(cartTotalGross)} nicht übersteigen
                                                </p>
                                            ) : isTierCappedByCart && cartTotalGross !== null ? (
                                                <p className='text-sm text-muted-foreground'>
                                                    Auf Warenkorbwert begrenzt ({formatEur(cartTotalGross)})
                                                </p>
                                            ) : cartTotalGross !== null ? (
                                                <p className='text-sm text-muted-foreground'>
                                                    Warenkorbwert: {formatEur(cartTotalGross)}
                                                </p>
                                            ) : null}
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )}

                        {selectedAppointment && (
                            <>
                                <Separator />
                                <BillingDraftAddressFields control={form.control} />
                            </>
                        )}

                        {/* Recipient */}
                        {selectedCustomer && (
                            recipientEmail ? (
                                <div className='flex items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-4'>
                                    <Mail className='h-5 w-5 text-primary shrink-0' />
                                    <div>
                                        <div className='text-sm text-muted-foreground'>Zahlungslink wird gesendet an</div>
                                        <div className='font-semibold'>{recipientEmail}</div>
                                    </div>
                                </div>
                            ) : (
                                <div className='flex items-center gap-3 rounded-md border border-destructive/50 bg-destructive/5 p-4 text-destructive'>
                                    <AlertCircle className='h-5 w-5 shrink-0' />
                                    <div className='text-sm font-medium'>
                                        Kunde hat keine E-Mail-Adresse hinterlegt. Bitte zuerst beim Kunden eine E-Mail-Adresse eintragen.
                                    </div>
                                </div>
                            )
                        )}

                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Abbrechen
                            </Button>
                            <Button type='submit' disabled={!canSubmit}>
                                {createAndSendMutation.isPending ? (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                ) : (
                                    <Send className='mr-2 h-4 w-4' />
                                )}
                                Ausfallentschädigung an Kunden senden
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};
